/**
 * Composant: Gestion de l'abonnement (local, sans Stripe Portal)
 */

import React, { useState } from 'react';
import { Calendar, Zap, TrendingDown, AlertCircle, Download } from 'lucide-react';
import { useSubscription } from '@/hooks/useSubscription';
import { useInvoices } from '@/hooks/useInvoices';
import { usePaymentMethods } from '@/hooks/usePaymentMethods';
import { supabase } from '@/lib/supabase';

interface ManageSubscriptionProps {
  onNavigateToBilling: () => void;
}

export default function ManageSubscription({ onNavigateToBilling }: ManageSubscriptionProps) {
  const subscription = useSubscription();
  const invoices = useInvoices();
  const paymentMethods = usePaymentMethods();
  
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const formatTokens = (tokens: number | undefined) => {
    if (tokens === undefined || tokens === null) return '0';
    return tokens.toLocaleString('fr-FR');
  };

  const handleCancel = async () => {
    try {
      setCancelling(true);
      await subscription.cancel();
      alert('✅ Abonnement résilié. Il restera actif jusqu\'à la fin de la période.');
      setShowCancelConfirm(false);
    } catch (error: any) {
      alert(`❌ Erreur: ${error.message}`);
    } finally {
      setCancelling(false);
    }
  };

  const openStripePortal = async () => {
    // Fallback pour modifier carte uniquement
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      if (!authSession) return;

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/billing/create-portal-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authSession.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      }
    } catch (error) {
      alert('Erreur lors de l\'ouverture du portail');
    }
  };

  return (
    <div className="space-y-6">
      {/* Modal Confirmation Résiliation */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-theme-surface border-2 border-red-500/30 rounded-2xl p-6 max-w-md w-full">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-theme-primary mb-2">Résilier l'abonnement ?</h3>
              <p className="text-theme-secondary text-sm">
                Votre abonnement restera actif jusqu'à la fin de la période en cours.
                Vous perdrez l'accès aux fonctionnalités premium après cette date.
              </p>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="w-full px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-bold rounded-xl transition-colors disabled:opacity-50"
              >
                {cancelling ? 'Résiliation...' : 'Confirmer la résiliation'}
              </button>
              <button
                onClick={() => setShowCancelConfirm(false)}
                disabled={cancelling}
                className="w-full px-6 py-3 bg-theme-hover hover:bg-theme-elevated text-theme-primary font-semibold rounded-xl transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Statut Abonnement */}
      <div className="p-6 rounded-2xl bg-theme-surface border border-theme-border">
        <h3 className="text-lg font-bold text-theme-primary mb-6">Mon abonnement</h3>
        
        {subscription.loading ? (
          <div className="text-center py-8 text-theme-secondary">Chargement...</div>
        ) : subscription.data?.subscription ? (
          <div className="space-y-6">
            {/* Card Statut */}
            <div className="p-6 rounded-xl bg-gradient-to-br from-accent-primary/10 to-accent-secondary/10 border border-accent-primary/30">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-sm text-theme-secondary mb-1">Plan actuel</div>
                  <div className="text-3xl font-black text-accent-primary uppercase">
                    {subscription.data.profile?.subscription_tier || 'FREE'}
                  </div>
                </div>
                <div className={`px-5 py-2.5 rounded-full text-sm font-bold ${
                  subscription.data.subscription?.status === 'active' 
                    ? 'bg-green-500/20 text-green-400 border-2 border-green-500/30' 
                    : 'bg-yellow-500/20 text-yellow-400 border-2 border-yellow-500/30'
                }`}>
                  {subscription.data.subscription?.status === 'active' ? '✓ Actif' : subscription.data.subscription?.status}
                </div>
              </div>

              {subscription.data.subscription?.current_period_end && (
                <div className="mt-4 pt-4 border-t border-accent-primary/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-theme-secondary mb-1">
                        {subscription.data.subscription?.cancel_at ? 'Fin d\'accès' : 'Prochain renouvellement'}
                      </div>
                      <div className="text-lg font-bold text-theme-primary">
                        {new Date(subscription.data.subscription.current_period_end).toLocaleDateString('fr-FR', {
                          day: 'numeric', month: 'long', year: 'numeric'
                        })}
                      </div>
                      {subscription.data.subscription?.cancel_at && (
                        <div className="text-xs text-yellow-400 mt-1">⚠️ Résiliation programmée</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Tokens */}
            {subscription.data.profile && (
              <div>
                <h4 className="text-sm font-semibold text-theme-secondary mb-3">Tokens ce mois</h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 rounded-xl bg-theme-hover/30 border border-theme-border/50">
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-4 h-4 text-accent-primary" />
                      <div className="text-xs text-theme-secondary">Allocation</div>
                    </div>
                    <div className="text-xl font-bold text-theme-primary">
                      {formatTokens(subscription.data.profile.subscription_tokens_monthly)}
                    </div>
                  </div>
                  <div className="p-4 rounded-xl bg-theme-hover/30 border border-theme-border/50">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingDown className="w-4 h-4 text-accent-tertiary" />
                      <div className="text-xs text-theme-secondary">Consommés</div>
                    </div>
                    <div className="text-xl font-bold text-accent-tertiary">
                      {formatTokens(subscription.data.profile.subscription_tokens_used)}
                    </div>
                  </div>
                  <div className="p-4 rounded-xl bg-theme-hover/30 border border-theme-border/50">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-accent-secondary" />
                      <div className="text-xs text-theme-secondary">Rollover</div>
                    </div>
                    <div className="text-xl font-bold text-accent-secondary">
                      {formatTokens(subscription.data.profile.tokens_rollover)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="grid md:grid-cols-2 gap-4">
              <button
                onClick={onNavigateToBilling}
                className="p-5 rounded-xl bg-gradient-to-r from-accent-primary to-accent-secondary hover:from-accent-secondary hover:to-accent-tertiary text-white transition-all"
              >
                <div className="font-bold text-lg">Changer de plan</div>
                <div className="text-sm opacity-90">Upgrade ou downgrade</div>
              </button>
              
              {!subscription.data.subscription?.cancel_at && (
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  className="p-5 rounded-xl bg-red-500/10 hover:bg-red-500/20 border-2 border-red-500/30 transition-all"
                >
                  <div className="font-bold text-lg text-red-400">Résilier</div>
                  <div className="text-sm text-red-300/70">Annuler l'abonnement</div>
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">📦</div>
            <div className="text-lg font-semibold text-theme-primary mb-2">Aucun abonnement actif</div>
            <button
              onClick={onNavigateToBilling}
              className="mt-4 px-8 py-4 bg-gradient-to-r from-accent-primary to-accent-secondary text-white font-bold rounded-xl"
            >
              Découvrir les plans
            </button>
          </div>
        )}
      </div>

      {/* Moyens de paiement */}
      <div className="p-6 rounded-2xl bg-theme-surface border border-theme-border">
        <h3 className="text-lg font-bold text-theme-primary mb-4">Moyens de paiement</h3>
        {paymentMethods.loading ? (
          <div className="text-center py-4">Chargement...</div>
        ) : paymentMethods.methods.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-theme-secondary text-sm mb-4">Aucune carte enregistrée</p>
            <button
              onClick={openStripePortal}
              className="px-6 py-3 bg-accent-primary text-white font-semibold rounded-xl"
            >
              Ajouter une carte
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {paymentMethods.methods.map((method) => (
              <div key={method.id} className="flex items-center justify-between p-4 rounded-xl bg-theme-hover/50">
                <div>
                  <div className="font-bold capitalize">{method.brand} •••• {method.last4}</div>
                  <div className="text-sm text-theme-secondary">
                    Expire {method.exp_month.toString().padStart(2, '0')}/{method.exp_year}
                  </div>
                </div>
                {method.is_default && (
                  <div className="px-3 py-1 bg-accent-primary/20 rounded-full text-xs text-accent-primary">
                    Par défaut
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Factures */}
      <div className="p-6 rounded-2xl bg-theme-surface border border-theme-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-theme-primary">Factures</h3>
          <Download className="w-5 h-5 text-theme-secondary" />
        </div>
        
        {invoices.loading ? (
          <div className="text-center py-8">Chargement...</div>
        ) : invoices.invoices.length === 0 ? (
          <div className="text-center py-8 text-theme-secondary">Aucune facture</div>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {invoices.invoices.map((invoice) => (
              <div key={invoice.id} className="flex items-center justify-between p-4 rounded-xl bg-theme-hover/50">
                <div className="flex-1">
                  <div className="font-bold text-sm">
                    Facture #{invoice.number || invoice.id.substring(3, 10)}
                  </div>
                  <div className="text-xs text-theme-secondary">
                    {new Date(invoice.created * 1000).toLocaleDateString('fr-FR')}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-lg font-bold">{(invoice.amount_paid / 100).toFixed(2)}€</div>
                  {invoice.invoice_pdf && (
                    <a
                      href={invoice.invoice_pdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg bg-accent-primary/20 hover:bg-accent-primary/30 text-accent-primary"
                    >
                      <Download className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

